import { z } from 'zod';

export const FREE_CREDITS_ON_SIGNUP = 5;
export const DEFAULT_DAILY_GEN_CAP = 50;
export const GLOBAL_HOURLY_USD_CAP = 200;

/**
 * Player tier. Determines daily-credit cap and (later) Coinbase-subscription
 * perks. The ledger lives in PlayerDO; this is the authoritative tier list.
 *
 * - free  — default for anyone who signs in. Capped at DEFAULT_DAILY_GEN_CAP.
 * - pro   — paid monthly subscriber. Daily cap doubled, multi-model unlock,
 *           private gallery (when those features ship).
 * - admin — operator / founder bypass. Effectively uncapped. Set via the
 *           OWNER_ADDRESSES env var on the worker.
 */
export const PLAYER_TIERS = ['free', 'pro', 'admin'] as const;
export type PlayerTier = (typeof PLAYER_TIERS)[number];

/** Effective daily-cap per tier. Admin is a sentinel — no real cap is checked. */
export const TIER_DAILY_CAP: Record<PlayerTier, number> = {
  free: DEFAULT_DAILY_GEN_CAP,
  pro: DEFAULT_DAILY_GEN_CAP * 2,
  admin: Number.MAX_SAFE_INTEGER,
};

/** A unit of metered work. Mapped 1:1 to the per-credit USD bill at default settings. */
export const CREDIT_USD_VALUE = 0.27; // retail per leaf at Standard tier ($20/75)

export const GenerationModeSchema = z.enum(['single', 'double', '4up', 'battle']);
export type GenerationMode = z.infer<typeof GenerationModeSchema>;

/**
 * Leaf cost per generation mode. Variants scale the cost (more gpt-image calls
 * = more leaves), so the default is one image for one leaf and extra takes are
 * paid: 2 variations for 2 leaves, 4 for 3 (a small bulk break), and battle
 * runs three different models for 3.
 */
export const MODE_CREDITS: Record<GenerationMode, number> = {
  single: 1,
  double: 2,
  '4up': 3,
  battle: 3,
};

/**
 * Fix mode pricing — surgical edit of one previously-generated image.
 *
 * First fix per generation set is FREE (tracked via parent.freeFixConsumed).
 * Subsequent fixes from the same set cost FIX_CREDITS each. Tunable — set to
 * 1 today (full single-mode price). If we move to half-credit pricing later,
 * change to 0.5 and ensure the PlayerDO ledger supports fractional credits.
 */
export const FIX_CREDITS = 1;

/**
 * Motion-clip pricing — the HD, watermark-free orbit clip a holder renders of
 * their own gilding (client-side; no provider spend). The free clip (watermarked,
 * lower-res) costs nothing; unlocking the HD no-watermark export is one leaf.
 */
export const MOTION_CLIP_CREDITS = 1;

export const CREDIT_PACKS = [
  {
    id: 'spark',
    label: 'Spark',
    priceUsd: 5,
    credits: 16,
    perks: ['16 gildings', '$0.31 per leaf'] as string[],
  },
  {
    id: 'standard',
    label: 'Standard',
    priceUsd: 20,
    credits: 75,
    perks: ['75 gildings', '$0.27 per leaf'],
  },
  {
    id: 'studio',
    label: 'Studio',
    priceUsd: 50,
    credits: 250,
    perks: ['250 gildings', 'Best price — $0.20 per leaf'],
  },
] as const;

export const CreditPackIdSchema = z.enum(['spark', 'standard', 'studio']);
export type CreditPackId = z.infer<typeof CreditPackIdSchema>;

/**
 * USDC-on-Base payment rails. Users pay by sending USDC to the operator's
 * treasury wallet on Base (native USDC, ~$0.01 gas); the worker verifies the
 * on-chain transfer and credits leaves. Base is chosen over mainnet purely for
 * fees + speed.
 */
export const BASE_CHAIN_ID = 8453;
export const BASE_CHAIN_ID_HEX = '0x2105';
/** Native USDC on Base (Circle), 6 decimals. */
export const USDC_BASE_ADDRESS = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';
export const USDC_DECIMALS = 6;
/** keccak256("Transfer(address,address,uint256)") — the ERC-20 Transfer topic. */
export const ERC20_TRANSFER_TOPIC =
  '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';

/** USDC base-units (6 dp) for a pack's USD price. */
export function usdcBaseUnits(priceUsd: number): bigint {
  return BigInt(Math.round(priceUsd * 10 ** USDC_DECIMALS));
}

export function getCreditPack(id: CreditPackId): (typeof CREDIT_PACKS)[number] {
  const pack = CREDIT_PACKS.find((p) => p.id === id);
  if (!pack) throw new Error(`unknown credit pack: ${id}`);
  return pack;
}
